import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useT } from '@transifex/react';

import { BIOMETRIC_FLAG, getConfigFeatureFlag } from '../config/runtimeConfig';
import { analytics } from '../utils/analytics';

import { attemptBiometricSignIn, type SignInResult } from './biometricSignIn';
import { isBiometricEnrolled, readBiometricUserEmail } from './biometricStorage';
import { featureLabelFor } from './featureLabel';
import { useBiometricCapabilities, type BiometricType } from './useBiometricCapabilities';

export type BiometricSignInState = {
  // True while we're figuring out whether biometric applies. Consumers gate
  // the render on this to avoid a flash of the password form for enrolled
  // users before the auto-trigger kicks in.
  checking: boolean;
  eligible: boolean;
  inFlight: boolean;
  prefillEmail: string | null;
  biometricType: BiometricType;
  attempt: () => Promise<SignInResult | null>;
  lastError: Extract<SignInResult, { ok: false }>['reason'] | null;
};

export const useBiometricSignIn = (opts: {
  onSuccess: (token: string) => void | Promise<void>;
}): BiometricSignInState => {
  const t = useT();
  const capabilities = useBiometricCapabilities();
  const [checking, setChecking] = useState<boolean>(Platform.OS !== 'web');
  const [eligible, setEligible] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [prefillEmail, setPrefillEmail] = useState<string | null>(null);
  const [lastError, setLastError] = useState<Extract<SignInResult, { ok: false }>['reason'] | null>(null);
  const autoTriggered = useRef(false);
  // Read via ref inside attempt() so inFlight flipping doesn't recreate the
  // callback (and re-fire the auto-trigger useEffect that depends on it).
  const inFlightRef = useRef(false);
  const onSuccessRef = useRef(opts.onSuccess);
  onSuccessRef.current = opts.onSuccess;

  // Native-only: reading the flag on web would consume a PostHog evaluation
  // for a code path this hook early-returns from below, muddying analytics.
  const flagOverride =
    Platform.OS === 'web' ? undefined : getConfigFeatureFlag(BIOMETRIC_FLAG);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (capabilities.loading) return;
    let cancelled = false;
    (async () => {
      try {
        if (!capabilities.available || !capabilities.enrolled) {
          if (!cancelled) setEligible(false);
          return;
        }
        // Skip the auto-prompt on devices where the OS prompt would dead-end.
        // See useBiometricCapabilities.usable and the Class 1/2/3 note there.
        // Fire an analytics event so the ME-1745 impact remains measurable —
        // the population this branch targets would otherwise vanish silently.
        if (!capabilities.usable) {
          analytics.trackEvent('biometric_signin_skipped', {
            reason: 'class_mismatch',
            platform: Platform.OS,
            enrolled_level: capabilities.enrolledLevel,
          });
          if (!cancelled) setEligible(false);
          return;
        }
        const flagOn = await analytics.isFeatureFlagEnabled(BIOMETRIC_FLAG);
        if (cancelled) return;
        if (!flagOn) {
          setEligible(false);
          return;
        }
        const enrolled = await isBiometricEnrolled();
        if (cancelled) return;
        if (!enrolled) {
          setEligible(false);
          return;
        }
        const email = await readBiometricUserEmail();
        if (cancelled) return;
        setPrefillEmail(email);
        setEligible(true);
      } catch {
        if (!cancelled) {
          setEligible(false);
          setPrefillEmail(null);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // flagOverride in deps: a host flipping the flag after mount re-runs
    // eligibility rather than freezing at the first-render value.
  }, [
    capabilities.loading,
    capabilities.available,
    capabilities.enrolled,
    capabilities.usable,
    capabilities.enrolledLevel,
    flagOverride,
  ]);

  const attempt = useCallback(async (): Promise<SignInResult | null> => {
    if (!eligible) return null;
    if (inFlightRef.current) return null;
    inFlightRef.current = true;
    setInFlight(true);
    setLastError(null);
    try {
      const featureLabel = featureLabelFor(t, capabilities.type);
      const result = await attemptBiometricSignIn({
        promptMessage: t('Sign in with {feature}', { feature: featureLabel }),
        cancelLabel: t('Cancel'),
      });
      if (result.ok) {
        await onSuccessRef.current(result.token);
      } else {
        setLastError(result.reason);
        if (result.reason === 'grant_invalid' || result.reason === 'no_grant_stored') {
          setEligible(false);
          setPrefillEmail(null);
        }
      }
      return result;
    } finally {
      inFlightRef.current = false;
      setInFlight(false);
    }
  }, [eligible, t, capabilities.type]);

  useEffect(() => {
    if (!eligible) return;
    if (autoTriggered.current) return;
    autoTriggered.current = true;
    attempt().catch(() => setLastError('exchange_failed'));
  }, [eligible, attempt]);

  return {
    checking,
    eligible,
    inFlight,
    prefillEmail,
    biometricType: capabilities.type,
    attempt,
    lastError,
  };
};
